import axios from 'axios';
import { API } from './api';

const KEY = 'fymua_admin_token';

export const getToken = () => localStorage.getItem(KEY) || '';
export const setToken = (t) => localStorage.setItem(KEY, t);
export const clearToken = () => localStorage.removeItem(KEY);

const admin = axios.create({ baseURL: `${API}/admin` });
admin.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
admin.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      clearToken();
      if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);

export const adminApi = {
  login: (email, password) => admin.post('/login', { email, password }).then(r => r.data),
  me: () => admin.get('/me').then(r => r.data),
  list: () => admin.get('/artists').then(r => r.data),
  create: (data) => admin.post('/artists', data).then(r => r.data),
  update: (slug, data) => admin.put(`/artists/${slug}`, data).then(r => r.data),
  toggleFlags: (slug, flags) => admin.patch(`/artists/${slug}/flags`, flags).then(r => r.data),
  remove: (slug) => admin.delete(`/artists/${slug}`).then(r => r.data),
  // CSV export / import
  exportCsvUrl: () => `${API}/admin/artists/export.csv`,
  exportCsvBlob: () => admin.get('/artists/export.csv', { responseType: 'blob' }).then(r => r.data),
  importPreview: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return admin.post('/artists/import-preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  importCommit: (rows, conflict_action) =>
    admin.post('/artists/import-commit', { rows, conflict_action }).then(r => r.data),
  // ImageKit
  imagekitAuth: (folder = 'findyourmua') => admin.get('/imagekit/auth', { params: { folder } }).then(r => r.data),
  // Site images
  listSiteImages: () => admin.get('/site-images').then(r => r.data),
  setSiteImage: (key, url) => admin.put(`/site-images/${key}`, { url }).then(r => r.data),
  removeSiteImage: (key) => admin.delete(`/site-images/${key}`).then(r => r.data),
  // Site settings (cities, featured order, default sort)
  listSettings: () => admin.get('/settings').then(r => r.data),
  setSetting: (key, value) => admin.put(`/settings/${key}`, { value }).then(r => r.data),
  // Analytics
  analytics: (from, to, granularity) => admin.get('/analytics', { params: { from, to, granularity } }).then(r => r.data),
  // Artist applications (New Artists review queue)
  listApplications: (status) => admin.get('/applications', { params: status ? { status } : {} }).then(r => r.data),
  applicationsCount: () => admin.get('/applications/count').then(r => r.data),
  getApplication: (id) => admin.get(`/applications/${id}`).then(r => r.data),
  patchApplication: (id, data) => admin.patch(`/applications/${id}`, data).then(r => r.data),
  generateBio: (id) => admin.post(`/applications/${id}/generate-bio`).then(r => r.data),
  approveApplication: (id, publish = true) => admin.post(`/applications/${id}/approve`, { publish }).then(r => r.data),
  rejectApplication: (id, reason = '') => admin.post(`/applications/${id}/reject`, { reason }).then(r => r.data),
  // Registration form config (Artist Form tab)
  getRegConfig: () => admin.get('/registration-config').then(r => r.data),
  setRegConfig: (key, value) => admin.put(`/registration-config/${key}`, { value }).then(r => r.data),
  // Form builder (custom fields, draft/publish)
  getFormBuilder: () => admin.get('/form-builder').then(r => r.data),
  saveFormDraft: (value) => admin.put('/form-builder', { value }).then(r => r.data),
  publishForm: () => admin.post('/form-builder/publish').then(r => r.data),
  // Locations (states/UTs + cities)
  getLocations: () => admin.get('/locations').then(r => r.data),
  saveLocations: (value) => admin.put('/locations', { value }).then(r => r.data),
  // Announcements (homepage strip)
  getAnnouncements: () => admin.get('/settings').then(r => r.data.announcements || []),
  saveAnnouncements: (value) => admin.put('/settings/announcements', { value }).then(r => r.data),
};
